import {
  AiImprovementJobStatusDto,
  CatalogSettingDto,
  SettingCategory,
  SettingDefinitionDto,
  SettingKey,
} from "@swagger/typescript-fetch-goflyer";
import { adminApiService } from "./api";
import { isAiImprovementNotFoundError } from "./aiImprovementSession";

export type AiImprovementJobFetchResult =
  | { type: "success"; job: AiImprovementJobStatusDto }
  | { type: "not_found" }
  | { type: "error"; cause: unknown };

const AI_CATEGORY = String(SettingCategory.AiImageImprovement);
const AI_PROMPT_KEY = SettingKey.AiImageImprovementPrompt;

const TERMINAL_STATES: AiImprovementJobStatusDto.StateEnum[] = [
  AiImprovementJobStatusDto.StateEnum.Completed,
  AiImprovementJobStatusDto.StateEnum.Failed,
];

export const isAiImprovementTerminal = (
  state: AiImprovementJobStatusDto.StateEnum,
): boolean => TERMINAL_STATES.includes(state);

export function normalizeAiImprovementJobStatus(
  response: AiImprovementJobStatusDto | AiImprovementJobStatusDto[],
): AiImprovementJobStatusDto | null {
  if (Array.isArray(response)) {
    if (response.length === 0) return null;
    return [...response].sort((a, b) => b.createdAt - a.createdAt)[0];
  }
  return response;
}

export async function fetchAiImprovementJobStatusSafe(
  id: string,
  authHeaders: { headers?: { Authorization?: string } },
): Promise<AiImprovementJobFetchResult> {
  try {
    const response =
      await adminApiService.gfImageAdminControllerGetAiImprovementJobStatus(
        id,
        authHeaders,
      );
    const job = normalizeAiImprovementJobStatus(
      response as AiImprovementJobStatusDto | AiImprovementJobStatusDto[],
    );
    if (!job) {
      return { type: "not_found" };
    }
    return { type: "success", job };
  } catch (cause) {
    if (isAiImprovementNotFoundError(cause)) {
      return { type: "not_found" };
    }
    return { type: "error", cause };
  }
}

/** @deprecated Prefer fetchAiImprovementJobStatusSafe */
export async function fetchAiImprovementJobStatus(
  id: string,
  authHeaders: { headers?: { Authorization?: string } },
): Promise<AiImprovementJobStatusDto | null> {
  const result = await fetchAiImprovementJobStatusSafe(id, authHeaders);
  if (result.type === "success") return result.job;
  return null;
}

export interface AiPromptSetting {
  systemDefault: string;
  userOverride: string | null;
  effective: string;
}

export async function getAiPromptSetting(
  authHeaders: { headers?: { Authorization?: string } },
): Promise<AiPromptSetting> {
  const [catalog, userSettings] = await Promise.all([
    adminApiService.gfAdminUserSettingsControllerGetCatalog(
      AI_CATEGORY,
      authHeaders,
    ),
    adminApiService.gfAdminUserSettingsControllerGetAdminUserSettings(
      AI_CATEGORY,
      authHeaders,
    ),
  ]);

  const catalogRow = (catalog as CatalogSettingDto[]).find(
    (r) => String(r.settingKey) === String(AI_PROMPT_KEY),
  );
  const override = (userSettings as SettingDefinitionDto[]).find(
    (v) => String(v.setting?.settingKey) === String(AI_PROMPT_KEY),
  );

  const systemDefault =
    typeof catalogRow?.defaultValue === "string"
      ? catalogRow.defaultValue
      : String(catalogRow?.defaultValue ?? "");

  const userOverride =
    override?.value != null
      ? typeof override.value === "string"
        ? override.value
        : String(override.value)
      : null;

  return {
    systemDefault,
    userOverride,
    effective: userOverride ?? systemDefault,
  };
}

export function parseAiImprovementProgress(progress?: unknown): number {
  if (typeof progress === "number" && !Number.isNaN(progress)) {
    return Math.min(100, Math.max(0, Math.round(progress)));
  }
  return 0;
}
